import { useResource } from "./useResource.ts"

export interface Texture {
  use(image: HTMLImageElement | null, location: WebGLUniformLocation): void
}

export function useTexture(name: string, gl: WebGL2RenderingContext): Texture {
  return useResource(function* () {
    const tex = gl.createTexture()
    gl.getExtension('GMAN_debug_helper')?.tagObject(tex, `Texture ${name}`)

    let priorImage: HTMLImageElement | null | undefined = undefined

    const object: Texture = {
      use(image: HTMLImageElement | null, location: WebGLUniformLocation) {
        const unit = 0
        gl.activeTexture(gl.TEXTURE0 + unit)
        gl.uniform1i(location, unit)

        gl.bindTexture(gl.TEXTURE_2D, tex)

        if (image !== priorImage) {
          // It is required to have a texture bound even if the program won't dynamically use it.
          if (image) {
            gl.texImage2D(
              gl.TEXTURE_2D, // target
              0, // mipmap level
              gl.RGBA, // internal format
              gl.RGBA, // format
              gl.UNSIGNED_BYTE, // type
              image, // pixels
            )
          } else {
            const pixel = new Uint8Array([0, 255, 0, 255])
            gl.texImage2D(
              gl.TEXTURE_2D, 0, gl.RGBA,
              1, 1, 0, // width, height, border
              gl.RGBA, gl.UNSIGNED_BYTE, pixel
            )
          }
          priorImage = image
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
          gl.generateMipmap(gl.TEXTURE_2D)
        }
      }
    }

    yield object

    gl.deleteTexture(tex)
  }, [gl])
}
